import React, { useState, useEffect } from "react";
import axios from "axios";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { jsPDF } from "jspdf";
import { usePdfSettings } from "../../../layouts/PdfSettingsContext.js";
import { useBranch } from '../../../layouts/BranchContext';
import {
  Button,
  Box,
  Tooltip,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  useTheme,
  Container,
  Modal,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";

import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { AttachFile as AttachFileIcon } from "@mui/icons-material";
import { decimalOnly } from "../../../utils/numericInput";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  overflowY: "auto",
  backgroundColor: theme.palette.background.paper,

  "&::-webkit-scrollbar": {
    width: "6px",
    background: "transparent",
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },

  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },

  scrollbarWidth: "thin",

  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

const AddEmployee = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pdfThemeColor, pdfTextColor } = usePdfSettings();

  const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();

  const [filteredDesignations, setFilteredDesignations] =
    useState([]);

  const [employeeImage, setEmployeeImage] =
    useState(null);

  const [resumeFile, setResumeFile] =
    useState(null);

 

  const [departments, setDepartments] =
    useState([]);

  const [designations, setDesignations] =
    useState([]);

  const [countries, setCountries] =
    useState([]);

  const [states, setStates] =
    useState([]);

  const [cities, setCities] =
    useState([]);

  const [allStates, setAllStates] =
    useState([]);

  const [allCities, setAllCities] =
    useState([]);

  const [filteredStates, setFilteredStates] =
    useState([]);

  const [filteredCities, setFilteredCities] =
    useState([]);

  const [showSuccess, setShowSuccess] =
    useState(false);

  const [openSnackbar, setOpenSnackbar] =
    useState(false);

  const [snackbarMessage, setSnackbarMessage] =
    useState("");

  const [snackbarSeverity, setSnackbarSeverity] =
    useState("success");

  const MAX_FILE_SIZE = 1 * 1024 * 1024;
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

  // ----------------------------------------------------
  // CURRENT DATE
  // ----------------------------------------------------

  const getCurrentDate = () => {
    const today = new Date();

    const year = today.getFullYear();

    const month = String(
      today.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      today.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // ----------------------------------------------------
  // FORM DATA
  // ----------------------------------------------------

  const [formData, setFormData] = useState({
    employeeName: "",
    dateOfBirth: "",
    dateOfJoining: getCurrentDate(),
    gender: "",
    mobileNo: "",
    alternateNo: "",
    email: "",
    department: "",
    designation: "",
    country: "",
    state: "",
    city: "",
    maritalStatus: "",
    organization: "",
    branch: selectedBranchId || "", // Use global branch ID
    address: "",
    salary: "",
  });

  // ----------------------------------------------------
  // MARITAL STATUS
  // ----------------------------------------------------

  const staticMaritalStatusData = [
    {
      statusId: 1,
      statusName: "Single",
    },
    {
      statusId: 2,
      statusName: "Married",
    },
  ];

  // ----------------------------------------------------
  // IMAGE CHANGE
  // ----------------------------------------------------

const handleImageChange = (e) => {
  const file = e.target.files?.[0];

  console.log("IMAGE SELECTED:", file);

  if (!file) {
    console.log("NO FILE SELECTED");
    return;
  }

  console.log("IMAGE NAME:", file.name);
  console.log("IMAGE TYPE:", file.type);
  console.log("IMAGE SIZE:", file.size);

  setEmployeeImage(file);
};
  // ----------------------------------------------------
  // RESUME CHANGE
  // ----------------------------------------------------

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setSnackbarMessage(
        "Resume size exceeds 1MB limit. Please choose a smaller file."
      );

      setSnackbarSeverity("error");
      setOpenSnackbar(true);

      e.target.value = "";
      return;
    }

    setResumeFile(file);
  };

  // ----------------------------------------------------
  // HANDLE CHANGE
  // ----------------------------------------------------

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

     // Ignore branch changes (handled globally)
  if (name === "branch") {
    return;
  }

    // ------------------------------------------------
    // MOBILE + ALTERNATE PHONE
    // ONLY DIGITS, MAX 10
    // ------------------------------------------------

    if (
      name === "mobileNo" ||
      name === "alternateNo"
    ) {
      const digitsOnly = String(value)
        .replace(/\D/g, "")
        .slice(0, 10);

      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));

      return;
    }

    // ------------------------------------------------
    // SALARY
    // POSITIVE NUMBER ONLY
    // ------------------------------------------------

    if (name === "salary") {
      setFormData((prevData) => ({
        ...prevData,
        salary: decimalOnly(value),
      }));

      return;
    }

    // ------------------------------------------------
    // DEPARTMENT
    // ------------------------------------------------

    if (name === "department") {
      setFormData((prevData) => ({
        ...prevData,
        department: value,
        designation: "",
      }));

      return;
    }

    // ------------------------------------------------
    // ORGANIZATION
    // ------------------------------------------------

    if (name === "organization") {
      setFormData((prevData) => ({
        ...prevData,
        organization: value,
        branch: "",
      }));

      return;
    }

    // ------------------------------------------------
    // BRANCH
    // ------------------------------------------------

    if (name === "branch") {
      setFormData((prevData) => ({
        ...prevData,
        branch: value,
      }));

      return;
    }

    // ------------------------------------------------
    // COUNTRY
    // ------------------------------------------------

    if (name === "country") {
      const selectedCountry =
        countries.find(
          (country) =>
            country &&
            String(
              country.countryName
            ) === String(value)
        );

      if (selectedCountry) {
        const statesForCountry =
          allStates.filter(
            (state) =>
              state &&
              String(
                state.countryId
              ) ===
                String(
                  selectedCountry.countryId
                )
          );

        setFilteredStates(
          statesForCountry
        );
      } else {
        setFilteredStates([]);
      }

      setFilteredCities([]);

      setFormData((prevData) => ({
        ...prevData,
        country: value,
        state: "",
        city: "",
      }));

      return;
    }

    // ------------------------------------------------
    // STATE
    // ------------------------------------------------

    if (name === "state") {
      const selectedState =
        allStates.find(
          (state) =>
            state &&
            String(
              state.stateName
            ) === String(value)
        );

      if (selectedState) {
        const citiesForState =
          allCities.filter(
            (city) =>
              city &&
              String(
                city.stateId
              ) ===
                String(
                  selectedState.stateId
                )
          );

        setFilteredCities(
          citiesForState
        );
      } else {
        setFilteredCities([]);
      }

      setFormData((prevData) => ({
        ...prevData,
        state: value,
        city: "",
      }));

      return;
    }

    // ------------------------------------------------
    // OTHER FIELDS
    // ------------------------------------------------

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

    // ----------------------------------------------------
  // GET STORED USER (organization comes from here)
  // ----------------------------------------------------

  const getStoredUser = () => {
    if (typeof window === "undefined") {
      return {};
    }

    try {
      return JSON.parse(
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
      );
    } catch (error) {
      console.error("Unable to read logged-in user:", error);
      return {};
    }
  };

    useEffect(() => {
    const user = getStoredUser();

    if (user?.organization_id) {
      setFormData((prevData) => ({
        ...prevData,
        organization: String(user.organization_id),
      }));
    }
  }, []);

  // ----------------------------------------------------
  // KEEP BRANCH IN SYNC WITH GLOBAL BRANCH CONTEXT
  // ----------------------------------------------------

  useEffect(() => {
    if (selectedBranchId) {
      setFormData((prevData) => ({
        ...prevData,
        branch: String(selectedBranchId),
      }));
    }
  }, [selectedBranchId]);

  // ----------------------------------------------------
  // FETCH DEPARTMENTS
  // ----------------------------------------------------

  useEffect(() => {
    const fetchDepartments =
      async () => {
        try {
          const accessToken =
            localStorage.getItem(
              "accessToken"
            );

          const response =
            await axios.get(
              `${API_BASE_URL}/api/master/departments/`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

          const rawData =
            Array.isArray(
              response?.data
            )
              ? response.data
              : Array.isArray(
                  response?.data?.results
                )
              ? response.data.results
              : Array.isArray(
                  response?.data?.data
                )
              ? response.data.data
              : [];

          const mappedDepartments =
            rawData
              .filter(Boolean)
              .map((dept) => ({
                departmentId:
                  dept?.id,
                departmentName:
                  dept?.name ||
                  dept?.department_name ||
                  "",
              }))
              .filter(
                (dept) =>
                  dept.departmentId !==
                    undefined &&
                  dept.departmentName
              );

          setDepartments(
            mappedDepartments
          );
        } catch (error) {
          console.error(
            "Error fetching departments:",
            error
          );

          setDepartments([]);
        }
      };

    fetchDepartments();
  }, []);

  // ----------------------------------------------------
  // FETCH DESIGNATIONS
  // ----------------------------------------------------

  useEffect(() => {
    const fetchDesignations =
      async () => {
        try {
          const accessToken =
            localStorage.getItem(
              "accessToken"
            );

          const response =
            await axios.get(
              `${API_BASE_URL}/api/master/designations/`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

          const rawData =
            Array.isArray(
              response?.data
            )
              ? response.data
              : Array.isArray(
                  response?.data?.results
                )
              ? response.data.results
              : Array.isArray(
                  response?.data?.data
                )
              ? response.data.data
              : [];

          const mappedDesignations =
            rawData
              .filter(Boolean)
              .map((desig) => ({
                designationId:
                  desig?.id,
                designationName:
                  desig?.name ||
                  desig?.designation_name ||
                  "",
                departmentId:
                  desig?.department ??
                  desig?.department_id ??
                  null,
              }))
              .filter(
                (desig) =>
                  desig.designationId !==
                    undefined &&
                  desig.designationName
              );

          setDesignations(
            mappedDesignations
          );
        } catch (error) {
          console.error(
            "Error fetching designations:",
            error
          );

          setDesignations([]);
        }
      };

    fetchDesignations();
  }, []);

  // ----------------------------------------------------
  // FETCH COUNTRIES
  // ----------------------------------------------------

  useEffect(() => {
    const fetchCountries =
      async () => {
        try {
          const accessToken =
            localStorage.getItem(
              "accessToken"
            );

          const response =
            await axios.get(
              `${API_BASE_URL}/api/master/countries/`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

          const rawData =
            Array.isArray(
              response?.data
            )
              ? response.data
              : Array.isArray(
                  response?.data?.results
                )
              ? response.data.results
              : Array.isArray(
                  response?.data?.data
                )
              ? response.data.data
              : [];

          const mappedCountries =
            rawData
              .filter(Boolean)
              .map((country) => ({
                countryId:
                  country?.id,
                countryName:
                  country?.name ||
                  country?.country_name ||
                  "",
              }))
              .filter(
                (country) =>
                  country.countryId !==
                    undefined &&
                  country.countryName
              );

          setCountries(
            mappedCountries
          );
        } catch (error) {
          console.error(
            "Error fetching countries:",
            error
          );

          setCountries([]);
        }
      };

    fetchCountries();
  }, []);

  // ----------------------------------------------------
  // FETCH STATES
  // ----------------------------------------------------

  useEffect(() => {
    const fetchStates =
      async () => {
        try {
          const accessToken =
            localStorage.getItem(
              "accessToken"
            );

          const response =
            await axios.get(
              `${API_BASE_URL}/api/master/states/`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

          const rawData =
            Array.isArray(
              response?.data
            )
              ? response.data
              : Array.isArray(
                  response?.data?.results
                )
              ? response.data.results
              : Array.isArray(
                  response?.data?.data
                )
              ? response.data.data
              : [];

          const mappedStates =
            rawData
              .filter(Boolean)
              .map((state) => ({
                stateId:
                  state?.id,
                stateName:
                  state?.name ||
                  state?.state_name ||
                  "",
                countryId:
                  state?.country ??
                  state?.country_id ??
                  null,
              }))
              .filter(
                (state) =>
                  state.stateId !==
                    undefined &&
                  state.stateName
              );

          setAllStates(
            mappedStates
          );

          setStates(
            mappedStates
          );
        } catch (error) {
          console.error(
            "Error fetching states:",
            error
          );

          setAllStates([]);
          setStates([]);
        }
      };

    fetchStates();
  }, []);

  // ----------------------------------------------------
  // FETCH CITIES
  // ----------------------------------------------------

  useEffect(() => {
    const fetchCities =
      async () => {
        try {
          const accessToken =
            localStorage.getItem(
              "accessToken"
            );

          const response =
            await axios.get(
              `${API_BASE_URL}/api/master/cities/`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

          const rawData =
            Array.isArray(
              response?.data
            )
              ? response.data
              : Array.isArray(
                  response?.data?.results
                )
              ? response.data.results
              : Array.isArray(
                  response?.data?.data
                )
              ? response.data.data
              : [];

          const mappedCities =
            rawData
              .filter(Boolean)
              .map((city) => ({
                cityId:
                  city?.id,
                cityName:
                  city?.name ||
                  city?.city_name ||
                  "",
                stateId:
                  city?.state ??
                  city?.state_id ??
                  null,
              }))
              .filter(
                (city) =>
                  city.cityId !==
                    undefined &&
                  city.cityName
              );

          setAllCities(
            mappedCities
          );

          setCities(
            mappedCities
          );
        } catch (error) {
          console.error(
            "Error fetching cities:",
            error
          );

          setAllCities([]);
          setCities([]);
        }
      };

    fetchCities();
  }, []);

  // ----------------------------------------------------
  // FILTER DESIGNATIONS
  // ----------------------------------------------------

  useEffect(() => {
    if (!formData.department) {
      setFilteredDesignations([]);
      return;
    }

    const selectedDept =
      departments.find(
        (dept) =>
          dept &&
          String(
            dept.departmentName
          ) ===
            String(
              formData.department
            )
      );

    if (!selectedDept) {
      setFilteredDesignations([]);
      return;
    }

    const filtered =
      designations.filter(
        (desig) =>
          desig &&
          String(
            desig.departmentId
          ) ===
            String(
              selectedDept.departmentId
            )
      );

    setFilteredDesignations(
      filtered
    );
  }, [
    formData.department,
    designations,
    departments,
  ]);

  // ----------------------------------------------------
  // SAVE
  // ----------------------------------------------------

  const handleSave = async (e) => {
    if (
      e &&
      typeof e.preventDefault ===
        "function"
    ) {
      e.preventDefault();
    }

    // ------------------------------------------------
    // REQUIRED FIELD CHECK
    // ------------------------------------------------

    const requiredFieldsFilled =
      Boolean(
        formData.employeeName &&
          formData.dateOfBirth &&
          formData.dateOfJoining &&
          formData.gender &&
          formData.mobileNo &&
          formData.email &&
          formData.department &&
          formData.designation &&
          formData.country &&
          formData.state &&
          formData.city &&
          formData.maritalStatus &&
          formData.organization &&
          formData.branch
      );

    if (!requiredFieldsFilled) {
      setSnackbarMessage(
        "Please fill all required fields!"
      );

      setSnackbarSeverity(
        "error"
      );

      setOpenSnackbar(true);

      return;
    }

    // ------------------------------------------------
    // MOBILE VALIDATION
    // ------------------------------------------------

    if (
      !/^\d{10}$/.test(
        formData.mobileNo
      )
    ) {
      setSnackbarMessage(
        "Mobile No. must contain exactly 10 digits."
      );

      setSnackbarSeverity(
        "error"
      );

      setOpenSnackbar(true);

      return;
    }

    // ------------------------------------------------
    // ALTERNATE NUMBER VALIDATION
    // ------------------------------------------------

    if (
      formData.alternateNo &&
      !/^\d{10}$/.test(
        formData.alternateNo
      )
    ) {
      setSnackbarMessage(
        "Alternate No. must contain exactly 10 digits."
      );

      setSnackbarSeverity(
        "error"
      );

      setOpenSnackbar(true);

      return;
    }

    // ------------------------------------------------
    // SALARY VALIDATION
    // ------------------------------------------------

    if (formData.salary) {
      const salaryValue =
        Number(
          formData.salary
        );

      if (
        !Number.isFinite(
          salaryValue
        ) ||
        salaryValue < 0
      ) {
        setSnackbarMessage(
          "Salary must be a positive number."
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(true);

        return;
      }
    }

    try {
      const formDataToSend =
        new FormData();

      // ------------------------------------------------
      // FILES
      // ------------------------------------------------

   if (employeeImage) {
  formDataToSend.append(
    "profile_photo",
    employeeImage
  );
}

      if (resumeFile) {
        formDataToSend.append(
          "resume",
          resumeFile
        );
      }

      // ------------------------------------------------
      // BASIC DETAILS
      // ------------------------------------------------

      formDataToSend.append(
        "name",
        formData.employeeName
      );

      formDataToSend.append(
        "address",
        formData.address
      );

      formDataToSend.append(
        "dob",
        formData.dateOfBirth
      );

      formDataToSend.append(
        "date_of_joining",
        formData.dateOfJoining
      );

      formDataToSend.append(
        "mobile",
        formData.mobileNo
      );

      formDataToSend.append(
        "gender",
        formData.gender
      );

      formDataToSend.append(
        "email",
        formData.email
      );

      formDataToSend.append(
        "phone",
        formData.alternateNo
      );

      formDataToSend.append(
        "marital_status",
        formData.maritalStatus.toLowerCase()
      );

           formDataToSend.append(
        "salary",
        formData.salary
      );

      // ------------------------------------------------
      // ORGANIZATION + BRANCH
      // ------------------------------------------------

      if (formData.organization) {
        formDataToSend.append(
          "organization",
          formData.organization
        );
      }

      if (formData.branch) {
        formDataToSend.append(
          "branch",
          formData.branch
        );
      }

      // ------------------------------------------------
      // SELECTED DEPARTMENT
      // ------------------------------------------------

      const selectedDepartment =
        departments.find(
          (dept) =>
            dept &&
            String(
              dept.departmentName
            ) ===
              String(
                formData.department
              )
        );

      if (
        selectedDepartment?.departmentId !==
        undefined
      ) {
        formDataToSend.append(
          "department",
          String(
            selectedDepartment.departmentId
          )
        );
      }

      // ------------------------------------------------
      // SELECTED DESIGNATION
      // ------------------------------------------------

      const selectedDesignation =
        designations.find(
          (desig) =>
            desig &&
            String(
              desig.designationName
            ) ===
              String(
                formData.designation
              )
        );

      if (
        selectedDesignation?.designationId !==
        undefined
      ) {
        formDataToSend.append(
          "designation",
          String(
            selectedDesignation.designationId
          )
        );
      }

      // ------------------------------------------------
      // SELECTED COUNTRY
      // ------------------------------------------------

      const selectedCountry =
        countries.find(
          (country) =>
            country &&
            String(
              country.countryName
            ) ===
              String(
                formData.country
              )
        );

      if (
        selectedCountry?.countryId !==
        undefined
      ) {
        formDataToSend.append(
          "country",
          String(
            selectedCountry.countryId
          )
        );
      }

      // ------------------------------------------------
      // SELECTED STATE
      // ------------------------------------------------

      const selectedState =
        allStates.find(
          (state) =>
            state &&
            String(
              state.stateName
            ) ===
              String(
                formData.state
              )
        );

      if (
        selectedState?.stateId !==
        undefined
      ) {
        formDataToSend.append(
          "state",
          String(
            selectedState.stateId
          )
        );
      }

      // ------------------------------------------------
      // SELECTED CITY
      // ------------------------------------------------

      const selectedCity =
        allCities.find(
          (city) =>
            city &&
            String(
              city.cityName
            ) ===
              String(
                formData.city
              )
        );

      if (
        selectedCity?.cityId !==
        undefined
      ) {
        formDataToSend.append(
          "city",
          String(
            selectedCity.cityId
          )
        );
      }

   
     

     

      // ------------------------------------------------
      // API REQUEST
      // ------------------------------------------------

      const accessToken =
        localStorage.getItem(
          "accessToken"
        );

      const response =
        await fetch(
          `${API_BASE_URL}/api/staff/employees/`,
          {
            method: "POST",

            headers: {
              Authorization: `Bearer ${accessToken}`,
            },

            body:
              formDataToSend,
          }
        );

      // ------------------------------------------------
      // RESPONSE
      // ------------------------------------------------

      let data = null;

      try {
        data =
          await response.json();
      } catch (jsonError) {
        data = null;
      }

      if (!response.ok) {
        if (
          data &&
          typeof data ===
            "object"
        ) {
          const errorMessages =
            [];

          Object.keys(
            data
          ).forEach(
            (key) => {
              if (
                Array.isArray(
                  data[key]
                )
              ) {
                data[
                  key
                ].forEach(
                  (msg) => {
                    errorMessages.push(
                      `${key}: ${msg}`
                    );
                  }
                );
              } else if (
                typeof data[key] ===
                "string"
              ) {
                errorMessages.push(
                  `${key}: ${data[key]}`
                );
              }
            }
          );

          setSnackbarMessage(
            errorMessages.length
              ? errorMessages.join(
                  ", "
                )
              : "Failed to save employee"
          );
        } else {
          setSnackbarMessage(
            "Failed to save employee"
          );
        }

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(
          true
        );

        return;
      }

      // ------------------------------------------------
      // SUCCESS
      // ------------------------------------------------

      setShowSuccess(true);
    } catch (error) {
      console.error(
        "Error saving employee:",
        error
      );

      setSnackbarMessage(
        "Failed to save employee"
      );

      setSnackbarSeverity(
        "error"
      );

      setOpenSnackbar(
        true
      );
    }
  };

  // ----------------------------------------------------
  // RESET
  // ----------------------------------------------------

  const handleCancel = () => {
    setFormData({
      employeeName: "",
      dateOfBirth: "",
      dateOfJoining:
        getCurrentDate(),
      gender: "",
      mobileNo: "",
      alternateNo: "",
      email: "",
      department: "",
      designation: "",
      country: "",
      state: "",
      city: "",
      maritalStatus: "",
      address: "",
      salary: "",
    });

    setFilteredStates([]);
    setFilteredCities([]);
    setEmployeeImage(null);
    setResumeFile(null);
  };

  // ----------------------------------------------------
  // BLANK EMPLOYEE FORM PDF (matches Admission PDF styling)
  // ----------------------------------------------------

  const handleDownloadBlankEmployeeForm = async () => {
    try {
      const branchId = selectedBranchId || formData.branch;
      const activeBranch = (branchList || []).find(
        (branch) => Number(branch.id) === Number(branchId)
      );
      let branchDetails = activeBranch || {};

      if (branchId) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/master/branch/${branchId}/`,
            {
              headers: {
                Authorization: `Bearer ${
                  localStorage.getItem("accessToken") ||
                  localStorage.getItem("access_token") ||
                  localStorage.getItem("token") ||
                  ""
                }`,
              },
            }
          );
          branchDetails = { ...branchDetails, ...(response.data || {}) };
        } catch (error) {
          console.warn("Unable to fetch branch details for blank employee form:", error);
        }
      }

      const organization = branchDetails?.organization_details ||
        branchDetails?.organization_data || branchDetails?.organization;
      const orgName = branchDetails?.organization_name ||
        branchDetails?.org_name ||
        (organization && typeof organization === "object"
          ? organization.name || organization.organization_name
          : "") || "INSTITUTION NAME";
      const branchName = branchDetails?.branch_name ||
        branchDetails?.name || branchDetails?.branch || "";
      const rawLogo = branchDetails?.logo_url ||
        branchDetails?.branch_logo_url || branchDetails?.logo ||
        branchDetails?.image_url || branchDetails?.image ||
        branchDetails?.branch_logo || "";
      const logoUrl = typeof rawLogo === "string"
        ? (/^https?:\/\//i.test(rawLogo)
            ? rawLogo
            : rawLogo
              ? `${String(API_BASE_URL || "").replace(/\/$/, "")}/${rawLogo.replace(/^\//, "")}`
              : "")
        : rawLogo?.url || rawLogo?.image_url || "";

      const loadImage = (url) => new Promise((resolve, reject) => {
        if (!url) return resolve(null);
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = image.naturalWidth || image.width;
            canvas.height = image.naturalHeight || image.height;
            canvas.getContext("2d").drawImage(image, 0, 0);
            resolve(canvas.toDataURL("image/png"));
          } catch (error) {
            reject(error);
          }
        };
        image.onerror = reject;
        image.src = url;
      });

      let logoBase64 = null;
      try {
        logoBase64 = await loadImage(logoUrl);
      } catch (error) {
        console.warn("Selected branch logo could not be embedded in the PDF:", error);
      }

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - margin * 2;
      const toRgb = (hex, fallback) => {
        if (typeof hex !== "string") return fallback;
        const clean = hex.trim().replace(/^#/, "");
        if (!/^[0-9a-f]{6}$/i.test(clean)) return fallback;
        return [
          parseInt(clean.slice(0, 2), 16),
          parseInt(clean.slice(2, 4), 16),
          parseInt(clean.slice(4, 6), 16),
        ];
      };
      const accent = toRgb(pdfThemeColor, [31, 78, 121]);
      const ink = toRgb(pdfTextColor, [38, 48, 58]);
      const muted = [95, 107, 119];
      const border = [185, 194, 204];
      const pale = accent.map((value) => Math.round(value + (255 - value) * 0.9));

      const drawSection = (title, y) => {
        doc.setFillColor(...pale);
        doc.roundedRect(margin, y, contentWidth, 6.5, 1, 1, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(...accent);
        doc.text(title.toUpperCase(), margin + 2.5, y + 4.4);
        return y + 9;
      };
      const drawField = (label, x, y, width, height = 10) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.6);
        doc.setTextColor(...ink);
        doc.text(label, x, y);
        doc.setDrawColor(...border);
        doc.setLineWidth(0.25);
        doc.line(x, y + height - 1.5, x + width, y + height - 1.5);
      };

      doc.setDrawColor(...accent);
      doc.setLineWidth(0.7);
      doc.line(margin, 8, pageWidth - margin, 8);
      if (logoBase64) {
        try { doc.addImage(logoBase64, "PNG", margin, 11, 17, 17, undefined, "FAST"); }
        catch (error) { console.warn("Unable to place branch logo in PDF:", error); }
      }

      // Passport-size photo placeholder, matching the Admission blank form.
      const photoW = 25;
      const photoH = 29;
      const photoX = pageWidth - margin - photoW;
      const photoY = 8;
      doc.setDrawColor(...border);
      doc.setLineWidth(0.35);
      doc.rect(photoX, photoY, photoW, photoH);
      const photoCenterX = photoX + photoW / 2;
      const photoCenterY = photoY + photoH / 2;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...muted);
      doc.setFontSize(6);
      doc.text("PASSPORT", photoCenterX, photoCenterY - 3, { align: "center" });
      doc.text("SIZE PHOTO", photoCenterX, photoCenterY, { align: "center" });
      doc.setFontSize(5.5);
      doc.text("(Paste here)", photoCenterX, photoCenterY + 4, { align: "center" });

      doc.setTextColor(...ink);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(doc.splitTextToSize(String(orgName), 115), pageWidth / 2, 16, {
        align: "center",
        maxWidth: 115,
      });
      if (branchName) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...muted);
        doc.text(doc.splitTextToSize(String(branchName), 115), pageWidth / 2, 22, {
          align: "center",
          maxWidth: 115,
        });
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...accent);
      doc.text("EMPLOYEE REGISTRATION FORM", pageWidth / 2, 31, { align: "center" });
      doc.setDrawColor(...border);
      doc.setLineWidth(0.3);
      doc.line(margin, 37, pageWidth - margin, 37);

      let y = 41;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...muted);
      doc.text("Please fill in BLOCK LETTERS and attach supporting documents where required.", margin, y);
      y += 5;

      const gap = 6;
      const colW = (contentWidth - gap) / 2;
      y = drawSection("Personal Details", y);
      drawField("Employee Full Name *", margin, y, colW);
      drawField("Date of Birth", margin + colW + gap, y, colW);
      y += 11;
      drawField("Gender", margin, y, colW);
      drawField("Marital Status", margin + colW + gap, y, colW);
      y += 11;
      drawField("Mobile Number *", margin, y, colW);
      drawField("Alternate Mobile Number", margin + colW + gap, y, colW);
      y += 11;
      drawField("Email Address", margin, y, contentWidth);
      y += 11;

      y = drawSection("Employment Details", y);
      drawField("Date of Joining", margin, y, colW);
      drawField("Department", margin + colW + gap, y, colW);
      y += 11;
      drawField("Designation", margin, y, colW);
      drawField("Monthly Salary", margin + colW + gap, y, colW);
      y += 11;
      drawField("Organization", margin, y, colW);
      drawField("Branch", margin + colW + gap, y, colW);
      y += 11;

      y = drawSection("Address Details", y);
      drawField("Address Line", margin, y, contentWidth);
      y += 11;
      drawField("Country", margin, y, colW);
      drawField("State", margin + colW + gap, y, colW);
      y += 11;
      drawField("City", margin, y, colW);
      drawField("Postal Code", margin + colW + gap, y, colW);
      y += 15;

      if (y > pageHeight - 35) {
        doc.addPage();
        y = 20;
      }
      doc.setDrawColor(...border);
      doc.line(margin, y + 6, margin + 55, y + 6);
      doc.line(pageWidth - margin - 55, y + 6, pageWidth - margin, y + 6);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...muted);
      doc.text("Employee Signature", margin + 27.5, y + 10, { align: "center" });
      doc.text("Authorized Signature", pageWidth - margin - 27.5, y + 10, { align: "center" });
      doc.setFontSize(6.5);
      doc.text("Generated blank form", pageWidth / 2, pageHeight - 7, { align: "center" });

      doc.save("Blank_Employee_Registration_Form.pdf");
    } catch (error) {
      console.error("Unable to generate blank employee form:", error);
      setSnackbarMessage("Unable to generate the blank PDF form. Please try again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  // ----------------------------------------------------
  // SUCCESS
  // ----------------------------------------------------

  const handleSuccessOk = () => {
    setShowSuccess(false);
    navigate(-1);
  };

  // ----------------------------------------------------
  // RENDER
  // ----------------------------------------------------

  return (
    <Container
      maxWidth="lg"
      sx={{
        my: 0,
      }}
    >
      <Box
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor:
            theme.palette
              .background.paper,
        }}
      >
        {/* HEADER */}

        <Box
          sx={{
            display:
              "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            mb: 3,
          }}
        >
        
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                mb: 0,
              }}
            >
              Employee Details
            </Typography>

            <Button
              variant="contained"
              onClick={handleDownloadBlankEmployeeForm}
              startIcon={<FileDownloadIcon />}
              sx={{
                minHeight: 42,
                borderRadius: 2,
                px: 2.5,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Download Blank Form (PDF)
            </Button>
       
        </Box>

        {/* SUCCESS MODAL */}

        {showSuccess ? (
          <Modal
            open={
              showSuccess
            }
            onClose={() =>
              setShowSuccess(
                false
              )
            }
            BackdropProps={{
              style: {
                backgroundColor:
                  "rgba(0, 0, 0, 0.8)",
              },
            }}
          >
            <Box
              sx={{
                position:
                  "absolute",
                top:
                  "50%",
                left:
                  "50%",
                transform:
                  "translate(-50%, -50%)",
                width: 400,
                bgcolor:
                  theme.palette
                    .background
                    .paper,
                borderRadius: 2,
                boxShadow: 24,
                overflow:
                  "hidden",
              }}
            >
              <Box
                sx={{
                  backgroundColor:
                    theme.palette
                      .primary
                      .main,
                  padding:
                    "12px 16px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      theme
                        .palette
                        .primary
                        .contrastText,
                  }}
                >
                  Employee Added ✅
                </Typography>
              </Box>

              <Box
                sx={{
                  padding:
                    "20px",
                }}
              >
                <Typography
                  sx={{
                    mb: 2,
                  }}
                >
                  Employee details have
                  been saved successfully.
                </Typography>

                <Box
                  sx={{
                    display:
                      "flex",
                    justifyContent:
                      "flex-end",
                    mt: 3,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={
                      handleSuccessOk
                    }
                  >
                    OK
                  </Button>
                </Box>
              </Box>
            </Box>
          </Modal>
        ) : (
          <form
            onSubmit={
              handleSave
            }
          >
            {/* IMAGE */}

            <Box
              sx={{
                mb: 1,
                display:
                  "flex",
                alignItems:
                  "center",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                component="label"
                startIcon={
                  <AttachFileIcon />
                }
              >
                Choose Image

                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={
                    handleImageChange
                  }
                />
              </Button>

              {employeeImage && (
                <Box
                  sx={{
                    display:
                      "flex",
                    alignItems:
                      "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                  >
                    {
                      employeeImage.name
                    }
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() =>
                      setEmployeeImage(
                        null
                      )
                    }
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            <Typography
              variant="caption"
              sx={{
                color:
                  theme.palette
                    .text.primary,
                fontWeight:
                  500,
                ml: 1,
                display:
                  "block",
                mb: 2,
                opacity: 0.7,
              }}
            >
              (Maximum file size: 1 MB)
            </Typography>

            {/* EMPLOYEE NAME */}

            <TextField
              label="Employee Name *"
              variant="outlined"
              fullWidth
              name="employeeName"
              value={
                formData.employeeName
              }
              onChange={
                handleChange
              }
              required
              sx={{
                mb: 3,
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },
                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
              }}
            />

            {/* DOB */}

            <TextField
              label="Date of Birth *"
              type="date"
              variant="outlined"
              fullWidth
              name="dateOfBirth"
              value={
                formData.dateOfBirth
              }
              onChange={
                handleChange
              }
              InputLabelProps={{
                shrink:
                  true,
              }}
              required
              sx={{
                mb: 3,
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },
                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
                "& .MuiInputBase-input[type='date']::-webkit-calendar-picker-indicator":
                  {
                    filter:
                      theme
                        .palette
                        .mode ===
                      "dark"
                        ? "invert(1)"
                        : "none",
                  },
              }}
            />

            {/* DOJ */}

            <TextField
              label="Date of Joining *"
              type="date"
              variant="outlined"
              fullWidth
              name="dateOfJoining"
              value={
                formData.dateOfJoining
              }
              onChange={
                handleChange
              }
              InputLabelProps={{
                shrink:
                  true,
              }}
              required
              sx={{
                mb: 3,
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },
                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
                "& .MuiInputBase-input[type='date']::-webkit-calendar-picker-indicator":
                  {
                    filter:
                      theme
                        .palette
                        .mode ===
                      "dark"
                        ? "invert(1)"
                        : "none",
                  },
              }}
            />

            {/* GENDER */}

            <FormControl
              component="fieldset"
              sx={{
                mb: 3,
              }}
              required
            >
              <Typography
                component="legend"
                sx={{
                  color:
                    theme.palette
                      .text.primary,
                }}
              >
                Gender *
              </Typography>

              <RadioGroup
                row
                name="gender"
                value={
                  formData.gender
                }
                onChange={
                  handleChange
                }
              >
                <FormControlLabel
                  value="male"
                  control={
                    <Radio
                      sx={{
                        color:
                          theme
                            .palette
                            .text
                            .primary,
                        "&.Mui-checked":
                          {
                            color:
                              theme
                                .palette
                                .primary
                                .main,
                          },
                      }}
                    />
                  }
                  label="Male"
                  sx={{
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  }}
                />

                <FormControlLabel
                  value="female"
                  control={
                    <Radio
                      sx={{
                        color:
                          theme
                            .palette
                            .text
                            .primary,
                        "&.Mui-checked":
                          {
                            color:
                              theme
                                .palette
                                .primary
                                .main,
                          },
                      }}
                    />
                  }
                  label="Female"
                  sx={{
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  }}
                />
              </RadioGroup>
            </FormControl>

            {/* MOBILE */}

            <TextField
              type="tel"
              inputProps={{
                maxLength: 10,
                inputMode:
                  "numeric",
                pattern:
                  "[0-9]*",
              }}
              label="Mobile No. *"
              variant="outlined"
              fullWidth
              name="mobileNo"
              value={
                formData.mobileNo
              }
              onChange={
                handleChange
              }
              required
              error={
                formData.mobileNo.length >
                  0 &&
                formData.mobileNo.length !==
                  10
              }
              helperText={
                formData.mobileNo.length >
                  0 &&
                formData.mobileNo.length <
                  10
                  ? `${
                      10 -
                      formData.mobileNo.length
                    } more digit(s) required`
                  : ""
              }
              sx={{
                mb: 3,
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },
                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
              }}
            />

            {/* ALTERNATE */}

            <TextField
              type="tel"
              inputProps={{
                maxLength: 10,
                inputMode:
                  "numeric",
                pattern:
                  "[0-9]*",
              }}
              label="Alternate No."
              variant="outlined"
              fullWidth
              name="alternateNo"
              value={
                formData.alternateNo
              }
              onChange={
                handleChange
              }
              error={
                formData.alternateNo.length >
                  0 &&
                formData.alternateNo.length !==
                  10
              }
              helperText={
                formData.alternateNo.length >
                  0 &&
                formData.alternateNo.length <
                  10
                  ? `${
                      10 -
                      formData.alternateNo.length
                    } more digit(s) required`
                  : ""
              }
              sx={{
                mb: 3,
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },
                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
              }}
            />

            {/* EMAIL */}

            <TextField
              label="Email *"
              variant="outlined"
              fullWidth
              name="email"
              value={
                formData.email
              }
              onChange={
                handleChange
              }
              required
              sx={{
                mb: 3,
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },
                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
              }}
            />

            {/* SALARY */}

            <TextField
              required
              label="Salary"
              variant="outlined"
              fullWidth
              name="salary"
              value={
                formData.salary
              }
              onChange={
                handleChange
              }
              inputProps={{
                inputMode:
                  "decimal",
                maxLength: 12,
              }}
              sx={{
                mb: 3,
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },
                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
              }}
            />

            {/* DEPARTMENT */}

            <FormControl
              fullWidth
              sx={{
                mb: 3,
              }}
              required
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,
                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                Department *
              </InputLabel>

              <Select
                name="department"
                value={
                  formData.department ||
                  ""
                }
                label="Department *"
                onChange={
                  handleChange
                }
                required
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },
                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                {departments
                  .filter(Boolean)
                  .map(
                    (department) => (
                      <MenuItem
                        key={
                          department.departmentId
                        }
                        value={
                          department.departmentName ||
                          ""
                        }
                      >
                        {
                          department.departmentName
                        }
                      </MenuItem>
                    )
                  )}
              </Select>
            </FormControl>

            {/* DESIGNATION */}

            <FormControl
              fullWidth
              sx={{
                mb: 3,
              }}
              required
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,
                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                Designation *
              </InputLabel>

              <Select
                name="designation"
                value={
                  formData.designation ||
                  ""
                }
                label="Designation *"
                onChange={
                  handleChange
                }
                required
                disabled={
                  !formData.department
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },
                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                {filteredDesignations
                  .filter(Boolean)
                  .map(
                    (designation) => (
                      <MenuItem
                        key={
                          designation.designationId
                        }
                        value={
                          designation.designationName ||
                          ""
                        }
                      >
                        {
                          designation.designationName
                        }
                      </MenuItem>
                    )
                  )}
              </Select>
            </FormControl>

            {/* COUNTRY */}

            <FormControl
              fullWidth
              sx={{
                mb: 3,
              }}
              required
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,
                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                Country *
              </InputLabel>

              <Select
                name="country"
                value={
                  formData.country ||
                  ""
                }
                label="Country *"
                onChange={
                  handleChange
                }
                required
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },
                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                {countries
                  .filter(Boolean)
                  .map(
                    (country) => (
                      <MenuItem
                        key={
                          country.countryId
                        }
                        value={
                          country.countryName ||
                          ""
                        }
                      >
                        {
                          country.countryName
                        }
                      </MenuItem>
                    )
                  )}
              </Select>
            </FormControl>

            {/* STATE */}

            <FormControl
              fullWidth
              sx={{
                mb: 3,
              }}
              required
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,
                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                State *
              </InputLabel>

              <Select
                name="state"
                value={
                  formData.state ||
                  ""
                }
                label="State *"
                onChange={
                  handleChange
                }
                required
                disabled={
                  !formData.country
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },
                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                {filteredStates
                  .filter(Boolean)
                  .map(
                    (state) => (
                      <MenuItem
                        key={
                          state.stateId
                        }
                        value={
                          state.stateName ||
                          ""
                        }
                      >
                        {
                          state.stateName
                        }
                      </MenuItem>
                    )
                  )}
              </Select>
            </FormControl>

            {/* CITY */}

            <FormControl
              fullWidth
              sx={{
                mb: 3,
              }}
              required
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,
                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                City *
              </InputLabel>

              <Select
                name="city"
                value={
                  formData.city ||
                  ""
                }
                label="City *"
                onChange={
                  handleChange
                }
                required
                disabled={
                  !formData.state
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },
                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                {filteredCities
                  .filter(Boolean)
                  .map(
                    (city) => (
                      <MenuItem
                        key={
                          city.cityId
                        }
                        value={
                          city.cityName ||
                          ""
                        }
                      >
                        {
                          city.cityName
                        }
                      </MenuItem>
                    )
                  )}
              </Select>
            </FormControl>

            {/* ADDRESS */}

            <TextField
              label="Address"
              variant="outlined"
              fullWidth
              name="address"
              value={
                formData.address
              }
              onChange={
                handleChange
              }
              sx={{
                mb: 3,
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },
                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },
                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
              }}
            />

            {/* MARITAL STATUS */}

            <FormControl
              fullWidth
              sx={{
                mb: 3,
              }}
              required
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,
                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                Marital Status *
              </InputLabel>

              <Select
                name="maritalStatus"
                value={
                  formData.maritalStatus ||
                  ""
                }
                label="Marital Status *"
                onChange={
                  handleChange
                }
                required
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },
                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                {staticMaritalStatusData
                  .filter(Boolean)
                  .map(
                    (status) => (
                      <MenuItem
                        key={
                          status.statusId
                        }
                        value={
                          status.statusName ||
                          ""
                        }
                      >
                        {
                          status.statusName
                        }
                      </MenuItem>
                    )
                  )}
              </Select>
            </FormControl>

            {/* RESUME */}

            <Box
              sx={{
                mb: 1,
                display:
                  "flex",
                alignItems:
                  "center",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                component="label"
                startIcon={
                  <AttachFileIcon />
                }
              >
                Choose Resume

                <input
                  type="file"
                  hidden
                  accept=".pdf"
                  onChange={
                    handleResumeChange
                  }
                />
              </Button>

              {resumeFile && (
                <Box
                  sx={{
                    display:
                      "flex",
                    alignItems:
                      "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                  >
                    {
                      resumeFile.name
                    }
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() =>
                      setResumeFile(
                        null
                      )
                    }
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            <Typography
              variant="caption"
              sx={{
                color:
                  theme.palette
                    .text.primary,
                fontWeight:
                  500,
                ml: 1,
                display:
                  "block",
                mb: 3,
                opacity: 0.7,
              }}
            >
              (Maximum file size: 1 MB)
            </Typography>

            {/* BUTTONS */}

            <Box
              sx={{
                display:
                  "flex",
                justifyContent:
                  "flex-end",
                gap: 2,
              }}
            >
              <Tooltip
                title={
                  !formData.employeeName
                    ? "Enter Employee Name"
                    : !formData.dateOfBirth
                    ? "Enter Date of Birth"
                    : !formData.dateOfJoining
                    ? "Enter Date of Joining"
                    : !formData.gender
                    ? "Select Gender"
                    : !formData.mobileNo
                    ? "Enter Mobile No."
                    : formData.mobileNo.length !==
                      10
                    ? "Mobile No. must contain 10 digits"
                    : !formData.email
                    ? "Enter Email"
                    : !formData.department
                    ? "Select Department"
                    : !formData.designation
                    ? "Select Designation"
                    : !formData.country
                    ? "Select Country"
                    : !formData.state
                    ? "Select State"
                    : !formData.city
                    ? "Select City"
                    : !formData.maritalStatus
                    ? "Select Marital Status"
                    : ""
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={
                      !formData.employeeName ||
                      !formData.dateOfBirth ||
                      !formData.dateOfJoining ||
                      !formData.gender ||
                      !formData.mobileNo ||
                      formData.mobileNo.length !==
                        10 ||
                      !formData.email ||
                      !formData.department ||
                      !formData.designation ||
                      !formData.country ||
                      !formData.state ||
                      !formData.city ||
                      !formData.maritalStatus 
                    }
                  >
                    SAVE
                  </Button>
                </span>
              </Tooltip>

              <Button
                variant="outlined"
                color="error"
                onClick={
                  handleCancel
                }
              >
                RESET
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                onClick={() =>
                  navigate(-1)
                }
              >
                BACK
              </Button>
            </Box>
          </form>
        )}
      </Box>

      {/* SNACKBAR */}

      <Snackbar
        open={
          openSnackbar
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setOpenSnackbar(
            false
          )
        }
        anchorOrigin={{
          vertical:
            "top",
          horizontal:
            "center",
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(
              false
            )
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width: "100%",
          }}
        >
          {
            snackbarMessage
          }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddEmployee;